// ABOUTME: Context provider for global keyboard shortcuts
// ABOUTME: Manages shortcut registration and help modal visibility

import { ShortcutsModal, type ShortcutCategory as UIShortcutCategory } from '@boundarylab/ui';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type Shortcut, useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

interface KeyboardShortcutsContextValue {
  showHelp: () => void;
  hideHelp: () => void;
  isHelpOpen: boolean;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | null>(null);

export function useKeyboardShortcutsContext() {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcutsContext must be used within KeyboardShortcutsProvider');
  }
  return context;
}

interface KeyboardShortcutsProviderProps {
  children: ReactNode;
}

export function KeyboardShortcutsProvider({ children }: KeyboardShortcutsProviderProps) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const navigate = useNavigate();

  const showHelp = useCallback(() => setIsHelpOpen(true), []);
  const hideHelp = useCallback(() => setIsHelpOpen(false), []);
  const toggleHelp = useCallback(() => setIsHelpOpen((prev) => !prev), []);

  // Global navigation shortcuts
  const globalShortcuts: Shortcut[] = [
    { key: '?', description: 'Show keyboard shortcuts', action: toggleHelp },
    { key: 'Escape', description: 'Close modal / cancel', action: hideHelp },
    { key: 'g', description: 'Go to Alpha Scanner', action: () => navigate('/') },
    { key: 'p', description: 'Go to Portfolios', action: () => navigate('/portfolios') },
    { key: 'w', description: 'Go to Watchlists', action: () => navigate('/watchlists') },
    { key: 's', description: 'Go to Symbols', action: () => navigate('/symbols') },
    { key: 'r', description: 'Go to Risk', action: () => navigate('/risk') },
    { key: 'f', description: 'Go to Factors', action: () => navigate('/factors') },
    { key: 'm', description: 'Go to Monitoring', action: () => navigate('/monitoring') },
  ];

  useKeyboardShortcuts(globalShortcuts);

  const displayCategories: UIShortcutCategory[] = useMemo(
    () => [
      {
        name: 'Navigation',
        shortcuts: [
          { key: 'g', description: 'Go to Alpha Scanner' },
          { key: 'p', description: 'Go to Portfolio' },
          { key: 'w', description: 'Go to Watchlist' },
          { key: 's', description: 'Go to Symbols' },
          { key: 'r', description: 'Go to Risk' },
          { key: 'f', description: 'Go to Factors' },
          { key: 'm', description: 'Go to Monitoring' },
        ],
      },
      {
        name: 'Alpha Scanner',
        shortcuts: [
          { key: '/', description: 'Focus search input' },
          { key: 'j', description: 'Move selection down' },
          { key: 'k', description: 'Move selection up' },
          { key: 'Enter', description: 'Expand/collapse selected row' },
        ],
      },
      {
        name: 'General',
        shortcuts: [
          { key: '?', description: 'Show keyboard shortcuts' },
          { key: 'Escape', description: 'Close modal / cancel' },
        ],
      },
    ],
    []
  );

  const value = useMemo(
    () => ({ showHelp, hideHelp, isHelpOpen }),
    [showHelp, hideHelp, isHelpOpen]
  );

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
      <ShortcutsModal isOpen={isHelpOpen} onClose={hideHelp} categories={displayCategories} />
    </KeyboardShortcutsContext.Provider>
  );
}
